import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { EditorContent, useEditor } from "@tiptap/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle, Button } from "@ndd/ui";
import { CircleCheck, Info } from "lucide-react";
import { useCallback, useState, type ReactElement } from "react";
import type {
  AppointmentDraftEditableResponse,
  AppointmentDraftResponse,
} from "../../../../shared/api-types";
import { richTextEditorExtensions } from "../../../../shared/rich-text-document";
import type {
  AppointmentDraftGenerationUi,
  AppointmentEnhanceActionState,
} from "@/lib/appointment-documentation-state";
import {
  copyAppointmentDraftAfterHeadCheck,
  type AppointmentDraftClipboardWrite,
  type AppointmentDraftCopyOutcome,
} from "@/lib/appointment-draft-copy";
import { isRevisionBoundDraftResponse } from "@/lib/appointment-draft-autosave";
import { authenticatedApi } from "@/lib/api";
import { deviceTimezone } from "@/lib/appointments";
import { clockOrDateLabel, draftProvenanceLine } from "@/lib/soap-draft-provenance";
import { soapDraftPath } from "@/lib/soap-draft-target";
import { NOTE_PROSE_CLASSES } from "@/components/v2/rich-text-editor";
import type { SoapDraftDelivery } from "@/lib/soap-draft-delivery";
import { AppointmentDraftSection, DraftCard } from "./appointment-draft-section";
import { CopyDraftButton } from "./copy-draft-button";
import { EditorFooter } from "./editor-status";
import {
  DraftProvenanceSentence,
  SoapDraftDeliveryStatus,
  SoapDraftFooterRow,
} from "./soap-draft-footer";
import { useNewerHealthieTranscript } from "./draft-transcript-notice";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

export function AppointmentDraftReadOnly({
  appointmentId,
  rdId,
  generation,
  delivery,
  documentationUnavailable,
  getToken,
}: {
  appointmentId: string;
  rdId: string;
  generation: AppointmentDraftGenerationUi;
  /** Healthie delivery state of the current approval (NO-541), read-only. */
  delivery: SoapDraftDelivery;
  documentationUnavailable: boolean;
  getToken: ClerkGetToken;
}): ReactElement | null {
  const ready = generation.phase === "draft_ready";
  const draft = useReadOnlyAppointmentDraft({ appointmentId, rdId, ready, getToken });
  if (documentationUnavailable) return <DocumentationUnavailable />;

  if (!ready) {
    return (
      <AppointmentDraftSection
        generation={generation}
        enhanceAction={"hidden" satisfies AppointmentEnhanceActionState}
        onRetry={ignoreRetry}
      />
    );
  }

  return (
    <ReadOnlyDraftQueryView
      draft={draft}
      delivery={delivery}
      selection={{ appointmentId, rdId }}
    />
  );
}

type ReadOnlyDraftController = ReturnType<typeof useReadOnlyAppointmentDraft>;

/** The selected Appointment and RD, so a delivered line can open the note in Healthie. */
interface ReadOnlyDraftSelection {
  appointmentId: string;
  rdId: string;
}

function ReadOnlyDraftQueryView({
  draft,
  delivery,
  selection,
}: {
  draft: ReadOnlyDraftController;
  delivery: SoapDraftDelivery;
  selection: ReadOnlyDraftSelection;
}): ReactElement {
  if (draft.query.isLoading) {
    return (
      <DraftCard>
        <DraftStatus>Opening the draft…</DraftStatus>
      </DraftCard>
    );
  }

  if (draft.query.isError) return <DraftReadError onReload={draft.query.refetch} />;

  if (!draft.query.data || draft.query.data.status === "not_editable") {
    return (
      <DraftCard>
        <DraftStatus>This saved draft can&apos;t be displayed yet.</DraftStatus>
      </DraftCard>
    );
  }

  return (
    <SavedDraftReadOnly
      key={draft.query.data.revision.id}
      draft={draft.query.data}
      delivery={delivery}
      selection={selection}
      copyCurrent={draft.copyCurrent}
    />
  );
}

function DraftReadError({ onReload }: { onReload: () => unknown }): ReactElement {
  return (
    <DraftCard>
      <Alert variant="danger" className="m-4">
        <AlertTitle>The draft didn&apos;t open.</AlertTitle>
        <AlertDescription className="mt-1">
          You can try again without changing anything.
          <div className="mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void onReload()}
            >
              Reload draft
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </DraftCard>
  );
}

function SavedDraftReadOnly({
  draft,
  delivery,
  selection,
  copyCurrent,
}: {
  draft: AppointmentDraftEditableResponse;
  delivery: SoapDraftDelivery;
  selection: ReadOnlyDraftSelection;
  copyCurrent: (
    clipboard: AppointmentDraftClipboardWrite
  ) => Promise<AppointmentDraftCopyOutcome>;
}): ReactElement {
  const editor = useEditor({
    extensions: richTextEditorExtensions,
    content: draft.document,
    editable: false,
    immediatelyRender: false,
  });

  return (
    <DraftCard>
      <div
        role="region"
        aria-label="Enhanced SOAP draft, read-only"
        tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto px-5 py-4"
      >
        <EditorContent editor={editor} className={NOTE_PROSE_CLASSES} />
      </div>
      <EditorFooter>
        <SoapDraftFooterRow
          status={
            <ReadOnlyStatusLines
              draft={draft}
              delivery={delivery}
              selection={selection}
            />
          }
          actions={<CopyDraftButton copyCurrent={copyCurrent} />}
        />
      </EditorFooter>
    </DraftCard>
  );
}

/** Provenance, the approval line, then what happened in Healthie once approved. */
function ReadOnlyStatusLines({
  draft,
  delivery,
  selection,
}: {
  draft: AppointmentDraftEditableResponse;
  delivery: SoapDraftDelivery;
  selection: ReadOnlyDraftSelection;
}): ReactElement {
  const [now] = useState(() => new Date().toISOString());
  const timezone = deviceTimezone();
  const newerHealthieTranscript = useNewerHealthieTranscript();
  const provenance = draftProvenanceLine(draft.provenance, {
    now,
    timezone,
    newerHealthieTranscript,
  });

  return (
    <>
      <DraftProvenanceSentence line={provenance} />
      <DraftApprovalStatus draft={draft} now={now} timezone={timezone} />
      {draft.review.state === "approved" ? (
        <SoapDraftDeliveryStatus
          delivery={delivery}
          appointmentId={selection.appointmentId}
          rdId={selection.rdId}
        />
      ) : null}
    </>
  );
}

function DraftApprovalStatus({
  draft,
  now,
  timezone,
}: {
  draft: AppointmentDraftEditableResponse;
  now: string;
  timezone: string;
}): ReactElement {
  if (draft.review.state === "approved") {
    return (
      <p role="status" className="flex items-start gap-2 text-body-sm leading-[18px]">
        <CircleCheck
          aria-hidden="true"
          className="mt-0.5 size-3.5 shrink-0 text-success"
        />
        <span className="font-medium text-foreground">
          Approved{" "}
          {clockOrDateLabel(draft.review.approval.approvedAt, { now, timezone })}
        </span>
      </p>
    );
  }

  return (
    <p
      role="status"
      className="flex items-start gap-2 text-body-sm leading-[18px] text-muted-foreground"
    >
      <Info aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      This saved draft is not approved.
    </p>
  );
}

function DraftStatus({ children }: { children: string }): ReactElement {
  return (
    <p role="status" className="px-5 py-4 text-body text-muted-foreground">
      {children}
    </p>
  );
}

function DocumentationUnavailable(): ReactElement {
  return (
    <DraftCard>
      <Alert variant="danger" className="m-4">
        <AlertTitle>Documentation status is unavailable.</AlertTitle>
        <AlertDescription className="mt-1">
          The Appointment Note is still available.
        </AlertDescription>
      </Alert>
    </DraftCard>
  );
}

function useReadOnlyAppointmentDraft(input: {
  appointmentId: string;
  rdId: string;
  ready: boolean;
  getToken: ClerkGetToken;
}) {
  const queryClient = useQueryClient();
  const queryKey = selectedDraftQueryKey(input.appointmentId, input.rdId);
  const target = {
    kind: "appointment",
    id: input.appointmentId,
    rdId: input.rdId,
  } as const;
  const query = useQuery({
    queryKey,
    queryFn: () => readSelectedAppointmentDraft(input.getToken, target),
    enabled: input.ready,
    staleTime: 0,
    gcTime: 0,
  });
  const copyCurrent = useCallback(
    async (
      clipboard: AppointmentDraftClipboardWrite
    ): Promise<AppointmentDraftCopyOutcome> => {
      const displayed = query.data;

      if (!displayed || displayed.status !== "editable") {
        await clipboard.cancel();

        return { status: "unavailable" };
      }

      const outcome = await copyAppointmentDraftAfterHeadCheck({
        readCurrent: async () => {
          try {
            return await readSelectedAppointmentDraft(input.getToken, target);
          } catch {
            return null;
          }
        },
        observeCurrent: (current) =>
          current.revision.id === displayed.revision.id ? "current" : "stale",
        clipboard,
      });
      if (outcome.status === "stale")
        queryClient.setQueryData(queryKey, outcome.current);

      return outcome;
    },
    [input.appointmentId, input.getToken, input.rdId, query.data, queryClient]
  );

  return { query, copyCurrent };
}

function selectedDraftQueryKey(appointmentId: string, rdId: string) {
  return ["appointment-draft-read-only", appointmentId, rdId] as const;
}

async function readSelectedAppointmentDraft(
  getToken: ClerkGetToken,
  target: { kind: "appointment"; id: string; rdId: string }
): Promise<AppointmentDraftResponse> {
  try {
    const response = await authenticatedApi<{ data: AppointmentDraftResponse }>(
      getToken,
      soapDraftPath(target)
    );

    if (
      response.data.status === "editable" &&
      !isRevisionBoundDraftResponse(response.data)
    ) {
      throw new Error("appointment_draft_response_invalid");
    }

    return response.data;
  } catch (error) {
    captureSelectedDraftReadFailure();
    throw error;
  }
}

function captureSelectedDraftReadFailure(): void {
  Sentry.captureException(new Error("appointment_draft_read_failed"), {
    tags: {
      operation: "read",
      source: "client",
      surface: "appointment_drafts",
    },
  });
}

function ignoreRetry(): void {}
